import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Checkbox, TextArea, TextInput, Select } from '../../../Form';
import Label from '../Label';
import clsx from 'clsx';
import useFieldVisibility from './useFieldVisibility';
import CountryField from '../CountryField';
import TaxIdField from '../TaxIdField';
import QuantityButton from '../QuantityButton';
import QuantityField from '../QuantityField';
import EmailField from '../EmailField';
import PhoneField from '../PhoneField';
import HiddenPhoneField from '../HiddenPhoneField';
import { AddressField } from '../../../AddressField';
import NumberInput from '../../../Form/components/NumberInput';
import StyledElement from '@repo/checkout/components/StyledElement';
import { FocusProvider } from '../../../../contexts/FocusContext';
import FocusableInput from './FocusableInput';
import fieldAutoComplete from './fieldAutoComplete';
import FieldDescription from '../FieldDescription';
import useAiTarget from '../../../../hooks/useAiTarget';
import MultiSelectField from '../MultiSelectField';
import { useMultiSelectRules } from '../useMultiSelectRules';
import { MULTI_SELECT_SEPARATOR, multiSelectOptionValue } from '@repo/lib';

const FormFieldItem = ({ className, field, horizontal, children }) => {
  const { isPreview, shouldHideField } = useFieldVisibility(field);
  const aiTarget = useAiTarget();

  if (shouldHideField && !isPreview) return null;

  return (
    <StyledElement
      elementPath="checkout.form.fields.field"
      as="div"
      {...aiTarget('field', { 'data-ai-field-id': field._id })}
      className={clsx(
        'cp_form-item mb-4',
        'bg-[var(--cp_checkout-form-fields-field-background-color)]',
        'text-[var(--cp_checkout-form-fields-field-color)]',
        className,
        {
          hidden: shouldHideField && !isPreview,
          'opacity-25': shouldHideField && isPreview,
          'flex items-center': horizontal,
        },
      )}
    >
      {children}
    </StyledElement>
  );
};

/**
 * The list layout of a multi-select: the Multiple choice markup with checkboxes
 * instead of radios, sharing the count rules with the dropdown layout.
 */
const MultiSelectList = ({ field, label, onChange, onBlur }) => {
  const options = field.options ?? [];
  const value = Array.isArray(field.value) ? field.value : [];
  const min = field.minValue?.enabled ? Number(field.minValue.value) || 0 : 0;
  const max = field.maxValue?.enabled ? Number(field.maxValue.value) || undefined : undefined;
  const { requiredInputRef, atMax } = useMultiSelectRules({
    selectedCount: value.length,
    required: field.required,
    min,
    max,
  });

  const chosenLabels = options
    .filter(option => value.includes(multiSelectOptionValue(option)))
    .map(option => option.label)
    .join(MULTI_SELECT_SEPARATOR);

  const toggle = optionValue => {
    const next = value.includes(optionValue)
      ? value.filter(item => item !== optionValue)
      : [...value, optionValue];
    onChange(options.map(multiSelectOptionValue).filter(item => next.includes(item)));
  };

  return (
    <FormFieldItem field={field} className={`cp_form-item cp_form-item-${field.element}`}>
      {label && (
        <StyledElement
          as={Label}
          elementPath="checkout.form.fields.label"
          htmlFor={field._id}
          className={clsx('cp_form-label')}
          required={field.required}
        >
          {label}
        </StyledElement>
      )}

      <fieldset data-testid={`multi-select-${field._id}`} data-layout="list">
        <legend className="sr-only">{label}</legend>

        <input
          ref={requiredInputRef}
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
          required={field.required}
          value={chosenLabels}
          onChange={() => {}}
          data-testid={`multi-select-${field._id}-required`}
        />

        <div className="space-y-1">
          {options.map(option => {
            const optionValue = multiSelectOptionValue(option);
            const checked = value.includes(optionValue);
            return (
              <div key={option._id ?? option.label} className="flex items-center">
                <input
                  onBlur={onBlur}
                  id={`${field._id}-${option._id ?? option.label}`}
                  name={field._id}
                  type="checkbox"
                  onChange={() => toggle(optionValue)}
                  value={optionValue}
                  checked={checked}
                  disabled={atMax && !checked}
                  className="cp_form-checkbox h-[18px] w-[18px] appearance-none disabled:opacity-40"
                />

                <StyledElement
                  as={Label}
                  elementPath="checkout.form.fields.label"
                  htmlFor={`${field._id}-${option._id ?? option.label}`}
                  className={clsx('cp_form-label cp_form-checkbox-label ml-2')}
                  style={{ marginBottom: '0px', paddingBottom: '0px' }}
                  checkEnabled={false}
                >
                  {option.label}
                </StyledElement>
              </div>
            );
          })}
        </div>
      </fieldset>
      <FieldDescription field={field} />
    </FormFieldItem>
  );
};

const FormField = ({ field: originalField, onChange, onBlur }) => {
  const { isPreview, shouldHideField } = useFieldVisibility(originalField);

  /**
   * In preview, hidden conditional fields stay in the DOM at opacity-25 so the
   * seller can still see them. We strip `required` so the browser doesn't fire
   * native validation ("Please fill in this field") on a field the customer
   * would never see.
   */
  const field = useMemo(
    () =>
      isPreview && shouldHideField && originalField.required
        ? { ...originalField, required: false }
        : originalField,
    [originalField, isPreview, shouldHideField],
  );

  if (originalField.disabled) return null;

  const label = field.label?.trim();

  switch (field.element) {
    case 'checkbox':
      return (
        <FocusProvider groupId={field._id}>
          <FormFieldItem
            field={field}
            horizontal
            style={{ display: 'flex', alignItems: 'center' }}
            className={`cp_form-item cp_form-item-${field.element}`}
          >
            <FocusableInput elementId={field._id}>
              <Checkbox
                id={field._id}
                name={field._id}
                type={field.element}
                value={field.value}
                onChange={e => onChange(e.target.value)}
                required={field.required}
                style={{ marginRight: '0.5em', marginBottom: '0' }}
                className="cp_form-checkbox"
                onBlur={onBlur}
              />
            </FocusableInput>

            {label && (
              <StyledElement
                as={Label}
                elementPath="checkout.form.fields.label"
                htmlFor={field._id}
                style={{ marginBottom: '0px', paddingBottom: '0px' }}
                className={clsx('cp_form-label cp_form-checkbox-label')}
                required={field.required}
                checkEnabled={false}
              >
                {label}
              </StyledElement>
            )}
          </FormFieldItem>
        </FocusProvider>
      );
    case 'textarea':
      return (
        <FocusProvider groupId={field._id}>
          <FormFieldItem field={field} className={`cp_form-item cp_form-item-${field.element}`}>
            {label && (
              <StyledElement
                as={Label}
                elementPath="checkout.form.fields.label"
                htmlFor={field._id}
                className={clsx('cp_form-label')}
                required={field.required}
              >
                {label}
              </StyledElement>
            )}
            <FocusableInput elementId={field._id}>
              <StyledElement
                elementPath="checkout.form.fields.input"
                as={TextArea}
                id={field._id}
                name={field._id}
                type={field.element}
                placeholder={field.placeholder || label}
                value={field.value}
                onChange={e => onChange(e.target.value)}
                required={field.required}
                className={clsx('cp_form-textarea')}
                onBlur={onBlur}
              />
            </FocusableInput>
            <FieldDescription field={field} />
          </FormFieldItem>
        </FocusProvider>
      );
    case 'number':
      return (
        <FocusProvider groupId={field._id}>
          <FormFieldItem field={field} className={`cp_form-item cp_form-item-${field.element}`}>
            {label && (
              <StyledElement
                as={Label}
                elementPath="checkout.form.fields.label"
                htmlFor={field._id}
                className={clsx('cp_form-label')}
                required={field.required}
              >
                {label}
              </StyledElement>
            )}

            <FocusableInput elementId={field._id}>
              <StyledElement
                elementPath="checkout.form.fields.input"
                as={NumberInput}
                id={field._id}
                name={field._id}
                placeholder={field.placeholder || label}
                type="number"
                value={field.value}
                onChange={e => onChange(e.target.value)}
                required={field.required}
                className={clsx('cp_form-textinput')}
                onBlur={onBlur}
              />
            </FocusableInput>
            <FieldDescription field={field} />
          </FormFieldItem>
        </FocusProvider>
      );
    case 'select':
      return (
        <FocusProvider groupId={field._id}>
          <FormFieldItem field={field} className={`cp_form-item cp_form-item-${field.element}`}>
            {label && (
              <StyledElement
                as={Label}
                elementPath="checkout.form.fields.label"
                htmlFor={field._id}
                className={clsx('cp_form-label')}
                required={field.required}
              >
                {label}
              </StyledElement>
            )}
            <FocusableInput elementId={field._id}>
              <StyledElement
                elementPath="checkout.form.fields.input"
                as={Select}
                onChange={e => {
                  const index = e.nativeEvent.target.selectedIndex;
                  const selectedOptionLabel = e.nativeEvent.target[index].text;

                  onChange(e.target.value, { selectedOptionLabel });
                }}
                key={field._id}
                required={field.required}
                value={field.value}
                defaultValue=""
                className={clsx('cp_form-select')}
                data-placeholder={field.value ? undefined : 'true'}
                onBlur={onBlur}
              >
                <option value="" disabled={field.required} className="cp_form-option">
                  {field.placeholder || 'Select an option'}
                </option>

                {field.options?.map(option => (
                  <option
                    value={option.value}
                    key={option._id}
                    className={`cp_form-option flex justify-between`}
                  >
                    {option.label}
                  </option>
                ))}
              </StyledElement>
            </FocusableInput>
            <FieldDescription field={field} />
          </FormFieldItem>
        </FocusProvider>
      );
    case 'multi-select':
      if (field.layout === 'list') {
        return <MultiSelectList field={field} label={label} onChange={onChange} onBlur={onBlur} />;
      }
      return (
        <FocusProvider groupId={field._id}>
          <FormFieldItem field={field} className={`cp_form-item cp_form-item-${field.element}`}>
            {label && (
              <StyledElement
                as={Label}
                elementPath="checkout.form.fields.label"
                htmlFor={field._id}
                className={clsx('cp_form-label')}
                required={field.required}
              >
                {label}
              </StyledElement>
            )}
            <FocusableInput elementId={field._id}>
              <StyledElement
                elementPath="checkout.form.fields.input"
                as={MultiSelectField}
                id={field._id}
                options={field.options ?? []}
                value={Array.isArray(field.value) ? field.value : []}
                required={field.required}
                min={field.minValue?.enabled ? Number(field.minValue.value) || 0 : 0}
                max={
                  field.maxValue?.enabled ? Number(field.maxValue.value) || undefined : undefined
                }
                placeholder={field.placeholder}
                onChange={values => onChange(values)}
                onBlur={onBlur}
              />
            </FocusableInput>
            <FieldDescription field={field} />
          </FormFieldItem>
        </FocusProvider>
      );
    case 'multiple-choice':
      return (
        <FormFieldItem field={field} className={`cp_form-item cp_form-item-${field.element}`}>
          {label && (
            <StyledElement
              as={Label}
              elementPath="checkout.form.fields.label"
              htmlFor={field._id}
              className={clsx('cp_form-label')}
              required={field.required}
            >
              {label}
            </StyledElement>
          )}

          <fieldset>
            <legend className="sr-only">{label}</legend>

            <div className="space-y-1">
              {field.options?.map(option => (
                <div key={option._id} className="flex items-center">
                  <input
                    onBlur={onBlur}
                    id={option._id}
                    name={field._id}
                    type="radio"
                    onChange={() => onChange(option.label)}
                    value={option.label}
                    checked={field.value === option.label}
                    required={field.required}
                    className="cp_form-radio h-[18px] w-[18px]"
                  />

                  <StyledElement
                    as={Label}
                    elementPath="checkout.form.fields.label"
                    htmlFor={option._id}
                    className={clsx('cp_form-label cp_form-radio-label ml-2')}
                    style={{ marginBottom: '0px', paddingBottom: '0px' }}
                    checkEnabled={false}
                  >
                    {option.label}
                  </StyledElement>
                </div>
              ))}
            </div>
          </fieldset>
          <FieldDescription field={field} />
        </FormFieldItem>
      );
    case 'quantity':
      return (
        <FocusProvider groupId={field._id}>
          <FormFieldItem
            field={field}
            horizontal
            className={`cp_form-item cp_form-item-${field.element}`}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            {label && (
              <StyledElement
                as={Label}
                elementPath="checkout.form.fields.label"
                htmlFor={field._id}
                style={{ marginBottom: '0px', paddingBottom: '0px' }}
                className={clsx('cp_form-label')}
                required={field.required}
              >
                {label}
              </StyledElement>
            )}

            <div className="flex items-stretch ml-2">
              <QuantityButton
                className="cp_quantity-button-minus"
                onClick={() => {
                  if (field.value <= 1) {
                    return;
                  }

                  onChange(Number(field.value - 1));
                  onBlur();
                }}
              >
                <MinusIcon className="cp_quantity-button-icon cp_quantity-button-minus-icon w-5 h-5" />
              </QuantityButton>

              <StyledElement
                elementPath="checkout.form.fields.input"
                as={QuantityField}
                id={field._id}
                name={field._id}
                value={field.value}
                onChange={onChange}
                required={true}
                min={field.minValue?.enabled ? field.minValue.value : 1}
                max={field.maxValue?.enabled ? field.maxValue.value : 9999}
              />

              <QuantityButton
                className="cp_quantity-button-plus"
                type="button"
                onClick={() => {
                  onChange(Number(field.value + 1));
                  onBlur();
                }}
              >
                <PlusIcon className="cp_quantity-button-icon cp_quantity-button-plus-icon w-5 h-5" />
              </QuantityButton>
            </div>
          </FormFieldItem>
        </FocusProvider>
      );
    case 'country':
      return (
        <FocusProvider groupId={field._id}>
          <FormFieldItem field={field} className={`cp_form-item cp_form-item-${field.element}`}>
            <CountryField field={field} onChange={onChange} onBlur={onBlur} />
            <FieldDescription field={field} />
          </FormFieldItem>
        </FocusProvider>
      );
    case 'tax-id':
      return (
        <FocusProvider groupId={field._id}>
          <FormFieldItem field={field} className={`cp_form-item cp_form-item-${field.element}`}>
            <TaxIdField field={field} onChange={onChange} onBlur={onBlur} />
            <FieldDescription field={field} />
          </FormFieldItem>
        </FocusProvider>
      );
    case 'email':
      return (
        <FocusProvider groupId={field._id}>
          <FormFieldItem field={field} className={`cp_form-item cp_form-item-${field.element}`}>
            <EmailField field={field} onChange={onChange} onBlur={onBlur} />
            <FieldDescription field={field} />
          </FormFieldItem>
        </FocusProvider>
      );
    case 'date-time': {
      return (
        <FocusProvider groupId={field._id}>
          <FormFieldItem field={field} className={`cp_form-item cp_form-item-${field.element}`}>
            {label && (
              <StyledElement
                as={Label}
                elementPath="checkout.form.fields.label"
                htmlFor={field._id}
                className={clsx('cp_form-label')}
                required={field.required}
              >
                {label}
              </StyledElement>
            )}

            <FocusableInput elementId={field._id}>
              <StyledElement
                elementPath="checkout.form.fields.input"
                as={TextInput}
                id={field._id}
                name={field._id}
                type="datetime-local"
                value={field.value}
                onChange={e => {
                  try {
                    const date = new Date(e.target.value);
                    if (isNaN(date.getTime())) {
                      return;
                    }

                    onChange(e.target.value);
                  } catch (error) {
                    return;
                  }
                }}
                required={field.required}
                className={clsx('cp_form-textinput')}
                min={field.minValue?.enabled ? field.minValue.value : ''}
                max={field.maxValue?.enabled ? field.maxValue.value : ''}
                onBlur={onBlur}
              />
            </FocusableInput>
            <FieldDescription field={field} />
          </FormFieldItem>
        </FocusProvider>
      );
    }
    case 'phone':
      if (shouldHideField && !isPreview) {
        return <HiddenPhoneField field={field} onChange={onChange} />;
      }
      return (
        <FocusProvider groupId={field._id}>
          <FormFieldItem field={field} className={`cp_form-item cp_form-item-${field.element}`}>
            <PhoneField field={field} onChange={onChange} onBlur={onBlur} />
            <FieldDescription field={field} />
          </FormFieldItem>
        </FocusProvider>
      );
    case 'address':
      return (
        <FormFieldItem field={field} className={`cp_form-item cp_form-item-${field.element}`}>
          <AddressField field={field} value={field.value} onChange={next => onChange(next)} />
        </FormFieldItem>
      );
    case 'po-number':
      return (
        <FocusProvider groupId={field._id}>
          <FormFieldItem field={field} className={`cp_form-item cp_form-item-${field.element}`}>
            {label && (
              <StyledElement
                as={Label}
                elementPath="checkout.form.fields.label"
                htmlFor={field._id}
                className={clsx('cp_form-label')}
                required={field.required}
              >
                {label}
              </StyledElement>
            )}

            <FocusableInput elementId={field._id}>
              <StyledElement
                elementPath="checkout.form.fields.input"
                as={TextInput}
                id={field._id}
                name={field._id}
                type="text"
                placeholder={field.placeholder || label}
                value={field.value}
                onChange={e => onChange(e.target.value)}
                required={field.required}
                className={clsx('cp_form-textinput')}
                min={field.minValue?.enabled ? field.minValue.value : ''}
                max={field.maxValue?.enabled ? field.maxValue.value : ''}
                onBlur={onBlur}
              />
            </FocusableInput>
            <FieldDescription field={field} />
          </FormFieldItem>
        </FocusProvider>
      );
    case 'text':
    default:
      return (
        <FocusProvider groupId={field._id}>
          <FormFieldItem field={field} className={`cp_form-item cp_form-item-${field.element}`}>
            {label && (
              <StyledElement
                as={Label}
                elementPath="checkout.form.fields.label"
                htmlFor={field._id}
                className={clsx('cp_form-label')}
                required={field.required}
              >
                {label}
              </StyledElement>
            )}

            <FocusableInput elementId={field._id}>
              <StyledElement
                elementPath="checkout.form.fields.input"
                as={TextInput}
                id={field._id}
                name={field._id}
                type={field.element}
                autoComplete={fieldAutoComplete(field)}
                placeholder={field.placeholder || label}
                value={field.value}
                onChange={e => onChange(e.target.value)}
                required={field.required}
                className={clsx('cp_form-textinput')}
                min={field.minValue?.enabled ? field.minValue.value : ''}
                max={field.maxValue?.enabled ? field.maxValue.value : ''}
                onBlur={onBlur}
              />
            </FocusableInput>
            <FieldDescription field={field} />
          </FormFieldItem>
        </FocusProvider>
      );
  }
};

FormField.propTypes = {
  onChange: PropTypes.func,
  field: PropTypes.shape({
    _id: PropTypes.string,
    element: PropTypes.oneOf(['text', 'textarea', 'email', 'select', 'multi-select', 'checkbox']),
    placeholder: PropTypes.string,
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    required: PropTypes.bool,
  }),
};

export default FormField;
